import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { useGatewayQuery } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { useMemo } from "react";
import { sortedAttendeesByOrganizerYouAndStatus } from "../utils/sortedAttendeesByOrganizerYouAndStatus";
import {
  EventPopoverDetailsDocument,
  EventPopoverDetailsQuery,
} from "./__generated__/EventPopoverDetails.v2.generated";

type EventPopoverDetails = {
  isFlexible: boolean;
  canMarkFlexible: boolean;
  loading: boolean;
  refetch: () => void;
  isOrganizer: boolean;
  description: string | undefined;
  attendees: EventDetailsAttendees;
};

type EventDetails = EventPopoverDetailsQuery["event"];
export type EventDetailsAttendees = Extract<EventDetails, { __typename: "Event" }>["attendees"];

export const useEventPopoverDetails = ({
  externalEventId,
  calendarId,
}: {
  externalEventId: string;
  calendarId: string;
}): EventPopoverDetails => {
  const { data, loading: loadingData, refetch } = useGatewayQuery(EventPopoverDetailsDocument, {
    variables: {
      id: externalEventId,
      calendarId,
    },
    fetchPolicy: "cache-and-network",
  });
  // `loadingData` will still be true if we hit the cache, so only show the loading spinner if we don't have any data.
  const loading = loadingData && !data;
  const isFlexible = !!data?.event?.flexibility?.isFlexible;
  const canMarkFlexible = !!data?.event?.canMarkFlexible;

  const currentUser = getValue(data?.currentUser, "User");
  const currentMember = getValue(currentUser?.member, "Member");
  const currentPerson = getValue(currentMember?.person, "Person");

  const event = getValue(data?.event, "Event");
  const organizer = getValue(event?.organizer, "Attendee");
  const organizerPerson = getValue(organizer?.person, "Person");

  const isOrganizer = currentPerson?.id === organizerPerson?.id;
  const description = data?.event?.description || undefined;

  // Attendee sorting matches event card attendee sorting
  const attendees = useMemo(
    () =>
      data?.event?.attendees
        ? sortedAttendeesByOrganizerYouAndStatus(data?.event?.attendees, calendarId)
        : [],
    [calendarId, data?.event?.attendees],
  );

  return useMemo(() => {
    return {
      isFlexible,
      canMarkFlexible,
      loading,
      isOrganizer,
      description,
      attendees,
      refetch,
    };
  }, [isFlexible, canMarkFlexible, loading, isOrganizer, description, attendees, refetch]);
};
